/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';
import { IoMdArrowBack } from 'react-icons/io';
import { FaRegPlayCircle, FaRegPauseCircle, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

import circleSound from 'assets/circle-sound.json';
import waveSound from 'assets/wave-sound.json';
import stopSound from 'assets/stop-sound.json';
import { useToggle } from 'utils/hooks/useToggle';
import showNotification from 'utils/showNotification';
import { handleTrackEvent } from 'utils/analytics';
import { useEvieAppContext } from '../EvieAppProvider';
import { formatTime } from '../utils';
import { useMarkContentAsCompleted } from '../Service/useMarkContentAsCompleted';

const AudioView = ({ isAssistantView, changeViewStatus, selectContent, selectedContent }) => {
  const { t } = useTranslation();
  const { refetch, activeProgram: { id } = {}, completedActivities = [] } = isAssistantView ? {} : useEvieAppContext();
  const { __typename, contentType, id: taskId, title, file: audioSrc, mediaUrl } = selectedContent;
  const currentTaskType = contentType || __typename; // contentType is used for chat recommended content, __typename is used for daily exercises task
  const currentAudioSrc = mediaUrl || audioSrc;
  const isCompleted = completedActivities.some((activity) => activity.id === taskId);
  const { markContentCompleted } = useMarkContentAsCompleted();

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useToggle(true);
  const audioRef = useRef(null);
  const audio = audioRef.current || {};

  useEffect(() => {
    const audioInstance = audioRef.current;

    const handleTimeUpdate = () => setCurrentTime(audioInstance.currentTime);
    const handleLoadedMetadata = () => setDuration(audioInstance.duration);

    audioInstance.addEventListener('timeupdate', handleTimeUpdate);
    audioInstance.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audioInstance.removeEventListener('timeupdate', handleTimeUpdate);
      audioInstance.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  const handlePlayPause = () => {
    setIsPlaying();
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const handleProgressChange = ({ prop, value }) => {
    if (prop === 'subtract') {
      setCurrentTime(audio.currentTime - value);
      audio.currentTime -= value;
    } else {
      setCurrentTime(audio.currentTime + value);
      audio.currentTime += value;
    }
  };

  const handleGoBack = () => {
    changeViewStatus();
    selectContent(null);
  };

  const handleAudioEnd = async () => {
    if (isCompleted || isAssistantView) {
      if (isAssistantView) handleTrackEvent('Recommendation completed', { type: currentTaskType, id: taskId, title });
      changeViewStatus();
      return;
    }

    try {
      const result = await markContentCompleted({
        contentId: taskId,
        contentType: currentTaskType,
        patientProgramId: id,
      });

      if (result.success) {
        await refetch();
        changeViewStatus();
        selectContent(null);
        showNotification({
          messages: [t('notification.completeAudio.success')],
        });
      } else {
        showNotification({
          messages: [result.error || t('notification.completeAudio.error')],
        });
      }
    } catch (error) {
      showNotification({
        messages: [t('notification.completeAudio.error')],
      });
    }
  };

  const radius = 90;
  const circumference = Math.PI * radius;
  const progress = (currentTime / duration) * circumference || 0;

  return (
    <section className="audio-view">
      <header className="audio-view__header">
        <IoMdArrowBack onClick={handleGoBack} />
      </header>
      <section className="audio-view__content">
        <div className="audio-view__content_animation">
          {isPlaying ? (
            <>
              <Lottie animationData={circleSound} loop />
              <Lottie animationData={waveSound} loop style={{ position: 'absolute', top: '0' }} />
            </>
          ) : (
            <Lottie animationData={stopSound} loop={false} />
          )}
        </div>
        <div style={{ maxWidth: '95%' }}>
          <h2 className="audio-view__content_title">{title}</h2>
          <p className="audio-view__content_duration">
            {formatTime(duration)} {t('evieApp.audio.timeUnit')}
          </p>
        </div>
      </section>
      <section className="audio-view__controls">
        <audio preload="auto" ref={audioRef} controls={false} autoPlay src={currentAudioSrc} onEnded={handleAudioEnd}>
          <track kind="captions" />
          Your browser does not support the
          <code>audio</code> element.
        </audio>
        <svg viewBox="0 0 200 100" className="h-auto" preserveAspectRatio="xMidYMid meet" width="100%">
          {/* Background fill */}
          <path d="M10,100 A90,90 0 0,1 190,100 L190,100 L10,100 Z" fill="#fff" />

          {/* Track arc */}
          <path d="M10,100 A90,90 0 0,1 190,100" fill="none" stroke="#ffeaf1" strokeWidth="8" />

          {/* Progress arc */}
          <path
            d="M10,100 A90,90 0 0,1 190,100"
            fill="none"
            stroke="#EA7DAE"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out',
            }}
          />
        </svg>
        <div className="audio-view__controls_playback">
          <p className="audio-view__controls_time">{formatTime(currentTime)}</p>
          <div className="audio-view__controls_btns">
            <button
              type="button"
              onClick={() => handleProgressChange({ prop: 'subtract', value: 5 })}
              className="audio-view__controls_back"
            >
              <FaAngleDoubleLeft />
            </button>
            <button type="button" onClick={handlePlayPause} className="audio-view__controls_play">
              {isPlaying ? <FaRegPauseCircle /> : <FaRegPlayCircle />}
            </button>
            <button
              type="button"
              onClick={() => handleProgressChange({ prop: 'add', value: 5 })}
              className="audio-view__controls_back"
            >
              <FaAngleDoubleRight />
            </button>
          </div>
        </div>
      </section>
    </section>
  );
};

AudioView.defaultProps = {
  isAssistantView: false,
};

AudioView.propTypes = {
  isAssistantView: PropTypes.bool,
  changeViewStatus: PropTypes.func.isRequired,
  selectContent: PropTypes.func.isRequired,
  selectedContent: PropTypes.shape({
    __typename: PropTypes.string,
    contentType: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    file: PropTypes.string,
    mediaUrl: PropTypes.string,
  }).isRequired,
};

export default AudioView;
